import {
	ResponsiveContainer,
	ComposedChart,
	XAxis,
	YAxis,
	Bar,
	Line,
	LabelList,
	Tooltip,
	BarChart,
	Legend,
} from "recharts";
import { numberFormatter } from "../../utils";
import moment from "moment";

interface Props {
	chartData: any;
	heading: any;
	isVisible: any;
}

interface T {
	quarter: string;
	actual: number;
	forecastedSpend: number;
	spendNeededForNextTier: number;
	spendShift: number;
	targetGrowthSpend: number;
}

const CustomLabel = (props: any) => {
	const { payload, dataLength, data } = props;
	const { x, y, value, index } = payload;
	const offset = 20;
	const shouldShow = index === 0 || index === dataLength - 1;
	if (!shouldShow) return null;
	const otherValue = data[index]?.sellPrice_Curr;
	const isHigher = value > otherValue;
	const labelY = isHigher ? y - offset : y + offset;
	const lineY2 = labelY;
	const textY = isHigher ? labelY - 5 : labelY + 5;
	return (
		<g>
			<line
				x1={x}
				y1={y}
				x2={x}
				y2={lineY2}
				stroke="#0097a9"
				opacity={0.5}
			/>
			<text
				x={x}
				y={textY}
				fill="#0097a9"
				className="graph-label-small"
				opacity={0.8}
				textAnchor="middle"
			>
				{numberFormatter(value, 1, "$")}
			</text>
		</g>
	);
};

const CustomLabel2 = (props: any) => {
	const { payload, dataLength, data } = props;
	const { x, y, value, index } = payload;
	const d = 20;
	const shouldShow = index === 0 || index === dataLength - 1;
	if (!shouldShow) return null;
	const otherValue = data[index]?.sellPrice_Prev;
	const isHigher = value >= otherValue;
	const labelY = isHigher ? y - d : y + d;
	const lineY2 = labelY;
	const textY = isHigher ? labelY - 5 : labelY + 5;
	return (
		<g>
			<line
				x1={x}
				y1={y}
				x2={x}
				y2={lineY2}
				stroke="#86bc25"
				opacity={0.5}
			/>
			<text
				x={x}
				y={textY}
				fill="#86bc25"
				className="graph-label-small"
				opacity={0.8}
				textAnchor="middle"
			>
				{numberFormatter(value, 1, "$")}
			</text>
		</g>
	);
};

const PricingExecution = (props: Props) => {
	let minValue = 0;
	let maxValue = 0;
	let newData: any = [];
	let data: any = props?.chartData?.finalData;
	if (data?.length > 0) {
		const minPrev = data.reduce((min: any, obj: any) => {
			return obj.sellPrice_Prev < min ? obj.sellPrice_Prev : min;
		}, Infinity);
		const minCurr = data.reduce((min: any, obj: any) => {
			return obj.sellPrice_Curr < min ? obj.sellPrice_Curr : min;
		}, Infinity);
		const maxPrev = data.reduce((max: any, obj: any) => {
			return obj.sellPrice_Prev > max ? obj.sellPrice_Prev : max;
		}, -Infinity);
		const maxCurr = data.reduce((max: any, obj: any) => {
			return obj.sellPrice_Curr > max ? obj.sellPrice_Curr : max;
		}, -Infinity);
		minValue = minPrev > minCurr ? minCurr : minPrev;
		maxValue = maxPrev > maxCurr ? maxPrev : maxCurr;
		data?.map((item: any, k: any) => {
			item.formattedWeek = moment.utc(item.month).format("MMM");
			if (item.monthCovered) {
				newData.push(item);
			}
		});
	}
	let heading = props?.heading;

	const footerData = [{ category: "2025" }, { category: "2026" }];

	const updateFill = (category: string) => {
		switch (category) {
			case "2025":
				return { color: "#0097a9", style: "solid" };
			case "2026":
				return { color: "#86bc25", style: "solid" };
			default:
				return { color: "black", style: "solid" };
		}
	};

	const updateLegend = (category: string) => {
		return category;
	};
	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "12vw",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				cursor: "pointer",
				color: "black",
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-between h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">Month</div>
						<div className="font-bold" style={{ color: "#3f3f3f" }}>
							{data.formattedWeek}
						</div>
					</div>
					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Sell Price 2026 ($/Unit):{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.sellPrice_Curr, 2, "$")}
							</label>
						</p>
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Sell Price 2025 ($/Unit):{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.sellPrice_Prev, 2, "$")}
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<div className="w-full h-full px-3">
			<div className="headSpace !h-[6vh]">
				<span className="headContent">Sell Price Index ($/Unit)</span>
			</div>
			<ResponsiveContainer
				width="100%"
				height={props.isVisible ? "69%" : "77%"}
				className="bg-[#ffff] px-[1vh] "
			>
				<ComposedChart
					data={newData}
					margin={{ top: 20, right: 20, left: 20 }}
				>
					<XAxis
						dataKey="formattedWeek"
						className="graph-label"
						stroke="black"
						interval={0}
					/>
					<YAxis
						hide
						domain={["dataMin - 1", "dataMax + 1"]}
						fontSize="1.5vh"
						tickFormatter={(value: number) =>
							numberFormatter(value, 0, "$")
						}
					/>
					<Tooltip
						contentStyle={{
							color: "black",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
						content={customTooltip}
					/>

					<Line
						type="linear"
						dataKey="sellPrice_Prev"
						stroke="#0097a9"
					name={`2026`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={3}
						dot={($) => (
							<CustomDot
								{...$}
								data={newData}
								color={"#0097a9"}
							/>
						)}
					>
						<LabelList
							dataKey="sellPrice_Prev"
							content={(props) => (
								<CustomLabel
									payload={props}
									data={newData}
									dataLength={newData.length}
								/>
							)}
						/>
					</Line>
					<Line
						type="linear"
						dataKey="sellPrice_Curr"
					name={`2026`}
						stroke="#86bc25"
						strokeDasharray={"0"}
						connectNulls={false}
						strokeWidth={3}
						dot={($) => (
							<CustomDot
								{...$}
								data={newData}
								color={"#86bc25"}
							/>
						)}
					>
						<LabelList
							dataKey="sellPrice_Curr"
							content={(props) => (
								<CustomLabel2
									payload={props}
									data={newData}
									dataLength={newData.length}
								/>
							)}
						/>
					</Line>
				</ComposedChart>
			</ResponsiveContainer>
			<div className="contentFooter h-[8vh]">
				<div className="flex flex-wrap items-start gap-x-2 gap-y-2 p-4 w-full">
					{footerData.map((entry, index) => {
						const { color, style } = updateFill(entry.category);
						return (
							<div
								key={`legend-${index}`}
								className="flex items-center min-h-[20px] cursor-pointer"
							>
								{style === "solid" && (
									<div
										className="w-3 h-3 mr-2"
										style={{ backgroundColor: color }}
									></div>
								)}
								<span
									className="text-[0.85em] leading-tight"
									style={{ color }}
								>
									{updateLegend(entry.category)}
								</span>
							</div>
						);
					})}
				</div>
			</div>
			<p className="bg-[#4d4d4d] w-full h-[0] flex justify-end px-[2vh] italic text-[1.8vh] font-normal relative top-[-3vh] text-black">
				Excludes new customers
			</p>
		</div>
	);
};

const CustomLegend = (props: any) => {
	const { payload } = props.payload;
	return (
		<ul className="custom-legend flex justify-center">
			{payload.map((entry: any, index: any) => (
				<>
					{
						<li
							key={`item-${index}`}
							style={{
								color:
									entry.value === "2025"
										? "#86bc25"
										: "#0097a9",
								marginLeft: 5,
								fontSize: "1.8vh",
							}}
						>
							<span
								style={{
									backgroundColor:
										entry.value === "2025"
											? "#86bc25"
											: "#0097a9",
									width: "1.3vh",
									height: 2,
									display: "inline-block",
									marginRight: 5,
									top: -4,
									position: "relative",
								}}
							/>
							{entry.value}
						</li>
					}
				</>
			))}
		</ul>
	);
};

const CustomDot = (props: any) => {
	const { cx, cy, index, data, color } = props;
	// Only render for first and last point
	if (index === 0 || index === data.length - 1) {
		return <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />;
	}
	return null;
};

export default PricingExecution;
